/* eslint-disable react/jsx-props-no-spreading */
import * as zod from 'zod';

import {
  Button,
  ButtonGroup,
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  socialLinkValidationMessages,
  socialLinkValidationPatterns,
} from '@/client/utils/socialLinks';
import { useLayoutEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import LearnerProfileCustomFieldsForm from '../forms/user-settings/LearnerProfileCustomFieldsForm';
import LearnerProfileSkillsForm from '../forms/user-settings/LearnerProfileSkillsForm';
import LearnerProfileSocialLinksForm from '../forms/user-settings/LearnerProfileSocialLinksForm';
import LearnerProfileUserDetailsForm from '../forms/user-settings/LearnerProfileUserDetailsForm';
import LearnerProfileUserLanguageForm from '../forms/user-settings/LearnerProfileUserLanguageForm';
import SolidButton from '@/client/components/shared/buttons/SolidButton';
import type { User as UserType } from '@/client/types/User';
import { learnApiClient } from '@/client/services/api/clients/learnApiClient';
import { stripHtmlTags } from '@/client/utils/stripHtmlTags';
import { useAuthStore } from '@/client/services/state/authStore';
import { useLearnerProfileStore } from '../state/learnerProfileStore';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

export interface UserDetailsFormData {
  firstName: string;
  lastName: string;
  email: string;
  about: string;
  language: string;
  skills: UserType['skills'];
  socialLinks: UserType['social_links'];
  metadata: UserType['metadata'];
}

interface LearnerProfileUserDetailsModalProps {
  userData: UserType;
}

export default function LearnerProfileUserDetailsFormModal({
  userData,
}: LearnerProfileUserDetailsModalProps) {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const queryClient = useQueryClient();
  const { authConfig } = useAuthStore();
  const { company, user } = authConfig;
  const { _id: userId } = user;
  const { feature_flags: featureFlags } = company;
  const { enable_social_profiles: enableSocialProfiles } = featureFlags;
  const { userDetailsModalIsOpen, setUserDetailsModalIsOpen, scrollToSection, setScrollToSection } =
    useLearnerProfileStore();

  const userDetailsSchema = zod.object({
    firstName: zod
      .string()
      .min(1, { message: `${t('learners.profile.userDetails.firstName.error')}` }),
    lastName: zod
      .string()
      .min(1, { message: `${t('learners.profile.userDetails.lastName.error')}` }),
    email: zod
      .string()
      .email({ message: `${t('learners.profile.userDetails.email.error')}` })
      .optional(),
    about: zod.string().optional(),
    language: zod.string().optional(),
    skills: zod
      .array(
        zod.object({
          _id: zod.string(),
          name: zod.string(),
        }),
      )
      .optional(),
    socialLinks: zod
      .array(
        zod.object({
          name: zod.string(),
          value: zod.string(),
          _id: zod.string(),
        }),
      )
      .superRefine((socialLinks, ctx) => {
        if (!socialLinks) return;

        socialLinks.forEach((link, index) => {
          if (!link.value.trim()) return; // Empty values are allowed

          const pattern =
            socialLinkValidationPatterns[link.name as keyof typeof socialLinkValidationPatterns];
          const message =
            socialLinkValidationMessages[link.name as keyof typeof socialLinkValidationMessages];

          if (pattern && !pattern.test(link.value)) {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              path: [index, 'value'],
              message: message || `${t('learners.profile.userDetails.socialLinks.error')}`,
            });
          }
        });
      })
      .optional(),
    metadata: zod.record(zod.any()).optional(),
  });

  // Refs for each section
  const aboutSectionRef = useRef<HTMLDivElement>(null);
  const languageSectionRef = useRef<HTMLDivElement>(null);
  const skillsSectionRef = useRef<HTMLDivElement>(null);
  const socialProfilesSectionRef = useRef<HTMLDivElement>(null);
  const customFieldsSectionRef = useRef<HTMLDivElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);

  // Scroll to section when modal opens
  useLayoutEffect(() => {
    if (userDetailsModalIsOpen && scrollToSection) {
      const sectionRefMap = {
        about: aboutSectionRef,
        'company-information': customFieldsSectionRef,
        'social-profiles': socialProfilesSectionRef,
        skills: skillsSectionRef,
      };

      const targetRef = sectionRefMap[scrollToSection as keyof typeof sectionRefMap];

      // Function to attempt scrolling
      const attemptScroll = () => {
        const targetElement = targetRef?.current;
        const modalBody = modalBodyRef.current;

        if (targetElement && modalBody) {
          // Calculate the scroll position with offset
          const targetOffsetTop = targetElement.offsetTop;
          const scrollTop = targetOffsetTop - 80; // 80px offset from top

          modalBody.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          });

          return true;
        }
        return false;
      };

      // Try immediately
      if (!attemptScroll()) {
        // If not successful, try with delays
        const timer1 = setTimeout(() => {
          if (!attemptScroll()) {
            const timer2 = setTimeout(attemptScroll, 200);
            return () => clearTimeout(timer2);
          }
        }, 100);

        return () => clearTimeout(timer1);
      }
    }
  }, [userDetailsModalIsOpen, scrollToSection]);

  const userFormDefaultValues: UserDetailsFormData = {
    firstName: userData.first_name,
    lastName: userData.last_name,
    email: userData.email,
    about: userData.about ? stripHtmlTags(userData.about) : '',
    language: userData.language,
    skills: userData.skills,
    socialLinks: userData.social_links,
    metadata: userData.metadata,
  };

  const methods = useForm({
    resolver: zodResolver(userDetailsSchema),
    defaultValues: userFormDefaultValues,
    mode: 'onChange',
  });

  // Register all form fields to ensure they're tracked by React Hook Form
  const { register } = methods;
  register('email');
  register('about');
  register('language');
  register('skills');
  register('socialLinks');
  register('metadata');

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isValid, dirtyFields },
  } = methods;

  const onClose = () => {
    reset();
    setUserDetailsModalIsOpen(false);
    setScrollToSection(null);
  };

  const updateUserDetailsMutation = useMutation({
    mutationFn: (changedFields: Record<string, any>) =>
      learnApiClient.post(`/users/${userId}`, changedFields),
    onSuccess: () => {
      queryClient.invalidateQueries(['learner-profile-user-data']);

      setUserDetailsModalIsOpen(false);

      setToast({
        show: true,
        status: 'success',
        title: t('learners.profile.userDetails.updateSuccess'),
      });
    },
    onError: () => {
      setToast({
        show: true,
        status: 'error',
        title: t('learners.profile.userDetails.updateError'),
      });
    },
  });

  const { status: updateUserDetailsStatus } = updateUserDetailsMutation;

  const onSubmit = (formData: UserDetailsFormData) => {
    // Get only the changed values
    const changedValues: Record<string, any> = {};

    // Check each field to see if it was modified
    Object.keys(dirtyFields).forEach((fieldName) => {
      changedValues[fieldName] = formData[fieldName as keyof UserDetailsFormData];
    });

    const restfulParamNameMap = {
      firstName: 'first_name',
      lastName: 'last_name',
      email: 'email',
      about: 'about',
      language: 'language',
      skills: 'skills',
      socialLinks: 'social_links',
      metadata: 'metadata',
    };

    const restfulParams = Object.keys(changedValues).reduce((acc, key) => {
      let value = changedValues[key];

      // If social links are present, strip _id from them
      if (key === 'socialLinks' && Array.isArray(value)) {
        value = value.map(({ _id, ...link }) => link);
      }

      return {
        ...acc,
        [restfulParamNameMap[key as keyof typeof restfulParamNameMap]]: value,
      };
    }, {});

    updateUserDetailsMutation.mutateAsync(restfulParams);
  };

  return (
    <Modal size="3xl" isOpen={userDetailsModalIsOpen} onClose={onClose} allowPinchZoom={false}>
      <ModalOverlay />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalContent>
            <ModalCloseButton />

            <ModalBody
              paddingTop={10}
              paddingBottom={8}
              paddingX={8}
              maxHeight={{ base: '600px', md: '700px' }}
              overflowY="auto"
              data-modal-body
              ref={modalBodyRef}
            >
              <Stack spacing={10}>
                <Text top={0} fontSize="28px" fontWeight={600} color="baseBlack">
                  {t('learners.profile.userDetails.editProfile')}
                </Text>

                <div ref={aboutSectionRef} data-section="about">
                  <LearnerProfileUserDetailsForm />
                </div>

                <Divider borderColor="neutral.100" />

                <div ref={languageSectionRef} data-section="language">
                  <LearnerProfileUserLanguageForm />
                </div>

                <Divider borderColor="neutral.100" />

                <div ref={skillsSectionRef} data-section="skills">
                  <LearnerProfileSkillsForm />
                </div>

                {enableSocialProfiles && (
                  <>
                    <Divider borderColor="neutral.100" />

                    <div ref={socialProfilesSectionRef} data-section="social-profiles">
                      <LearnerProfileSocialLinksForm />
                    </div>
                  </>
                )}

                <Divider borderColor="neutral.100" />

                <div ref={customFieldsSectionRef} data-section="company-information">
                  <LearnerProfileCustomFieldsForm />
                </div>
              </Stack>
            </ModalBody>

            <ModalFooter paddingY={6} paddingX={8} boxShadow="0 -4px 8px 0 rgba(0, 0, 0, 0.04)">
              <ButtonGroup>
                <Button variant="cancel" onClick={onClose}>
                  {t('learners.profile.userDetails.cancel')}
                </Button>

                <SolidButton
                  type="submit"
                  isDisabled={!isDirty || !isValid}
                  isLoading={updateUserDetailsStatus === 'loading'}
                >
                  {t('learners.profile.userDetails.saveChanges')}
                </SolidButton>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </form>
      </FormProvider>
    </Modal>
  );
}
